import {
  checkAnswer,
  pressing,
  takesAnswers,
  type JourneyRuntime,
  type Readiness,
} from '@cyberscore/contracts';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock,
  Info,
  MessagesSquare,
  PartyPopper,
  Send,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { href, navigate } from '../../lib/router';
import { AnswerInput } from './AnswerInput';
import { useAnswer, useAssessment, useJourney, useQuestions } from './api';
import { useFinish } from './finish';
import {
  counts,
  isAskable,
  nextQuestion,
  plainMessage,
  remaining,
  valueErrors,
  type QuestionRow,
} from './flow';
import { StageIcon, stageNote } from './stages';
import { useChangeRequests } from '../review/api';

/** Optional questions skipped in this browser session, per assessment (so "next" moves past them). */
function useSkipped(id: string) {
  const store = `cyberscore.skipped.${id}`;
  const [skipped, setSkipped] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(store) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  const update = (next: Set<string>) => {
    setSkipped(next);
    try {
      sessionStorage.setItem(store, JSON.stringify([...next]));
    } catch {
      /* storage unavailable: skips last for this page only */
    }
  };
  return [skipped, update] as const;
}

/**
 * Answering, one question at a time, in the order the journey sets: the first unanswered required question
 * in the earliest open stage, then optional ones. Choices save and move on with one tap; Back is the
 * browser's back.
 */
export function QuestionFlow({ id, question }: { id: string; question: string | null }) {
  const detail = useAssessment(id);
  const qs = useQuestions(id);
  const journey = useJourney(id, !!detail.data?.journey_key);
  const [skipped, setSkipped] = useSkipped(id);
  const [feedback, setFeedback] = useState<{ title: string; text: string } | null>(null);
  // Kept here, not in the card: a conflict refetches the question, which remounts the card at the new version.
  const [conflict, setConflict] = useState<string | null>(null);

  const rows = qs.data?.questions;
  // With the business for changes (task 61): only the answers the insurer asked about, in its order.
  const remediation = detail.data?.status === 'in_remediation';
  const changes = useChangeRequests(id, remediation);
  const asked = new Map(
    (changes.data?.requests ?? []).filter((r) => r.state === 'sent').map((r) => [r.question_key, r]),
  );
  const current =
    question ??
    (rows && remediation
      ? changes.data ? ([...asked.values()].find((r) => !r.addressed && !skipped.has(r.question_key))?.question_key ?? null) : null
      : rows ? nextQuestion(rows, skipped, qs.data?.queue) : null); // prettier-ignore

  // Put the question in the address, so Back returns to it rather than to "whatever is next".
  useEffect(() => {
    if (!question && current) navigate(href.answer(id, current), { replace: true });
  }, [question, current, id]);

  if (detail.isPending || qs.isPending || (remediation && changes.isPending))
    return <div className="empty">Loading…</div>;
  if (detail.isError || qs.isError)
    return (
      <div className="empty">
        <strong>Could not load this assessment</strong>
        {(detail.error ?? qs.error)?.message}
      </div>
    );

  const crumbs = (
    <div className="row-between wrap">
      <div className="crumbs">
        <a href={href.assessments()}>Assessments</a>
        <ChevronRight size={14} />
        <a href={href.assessment(id)}>{detail.data.customer_name}</a>
        <ChevronRight size={14} />
        Answer
      </div>
      {detail.data.status === 'in_progress' && (
        <a className="crumbs flow-chat" href={href.chat(id)}>
          <MessagesSquare size={14} /> Prefer to chat? Answer in chat
        </a>
      )}
    </div>
  );
  const row = current ? rows!.find((r) => r.key === current) : undefined;

  return (
    <>
      {crumbs}
      <div className="flow-layout">
        {row ? (
          <div className="stack">
            {asked.get(row.key) && (
              <div className="notice" role="note">
                <b>Your insurer asked:</b> “{asked.get(row.key)!.message}”
              </div>
            )}
            <QuestionCard
              key={`${row.key}:${row.based_on}`}
              id={id}
              row={row}
              rows={rows!}
              journey={journey.data ?? null}
              enough={qs.data.readiness.enough}
              closed={!takesAnswers(detail.data.status) || (remediation && !asked.has(row.key))}
              onSkip={() => {
                setSkipped(new Set(skipped).add(row.key));
                navigate(href.answer(id));
              }}
              onFeedback={setFeedback}
              conflict={conflict === row.key}
              onConflict={(on) => setConflict(on ? row.key : null)}
            />
          </div>
        ) : remediation ? (
          <section className="card qcard">
            <h2>That’s every change your insurer asked for</h2>
            <p className="muted">Send it back to them when you’re ready.</p>
            <a className="btn btn-primary" href={href.assessment(id)}>
              Review and send back
            </a>
          </section>
        ) : current ? (
          <section className="card qcard">
            <h2>That question is not part of this assessment</h2>
            <a className="btn" href={href.answer(id)}>
              Go to the next question
            </a>
          </section>
        ) : (
          <Finished id={id} rows={rows!} skipped={skipped} onRevisit={() => setSkipped(new Set())} />
        )}
        <Progress
          rows={rows!}
          journey={journey.data ?? null}
          current={row}
          feedback={feedback}
          readiness={qs.data.readiness}
        />
      </div>
    </>
  );
}

function QuestionCard({
  id,
  row,
  rows,
  journey,
  enough,
  closed,
  onSkip,
  onFeedback,
  conflict,
  onConflict,
}: {
  id: string;
  row: QuestionRow;
  rows: QuestionRow[];
  journey: JourneyRuntime | null;
  /** Enough for a reliable score already: an optional question says it can be left (task 86). */
  enough: boolean;
  closed: boolean;
  onSkip: () => void;
  onFeedback: (f: { title: string; text: string } | null) => void;
  conflict: boolean;
  onConflict: (on: boolean) => void;
}) {
  // prettier-ignore
  const q = row.definition;
  const answer = useAnswer(id);
  const [draft, setDraft] = useState<unknown>(row.answer?.value);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const stage = journey?.stages.find((s) => s.key === row.stage?.key);
  const askable = isAskable(row);
  const blocked = closed || !askable || answer.isPending;

  const textOf = (k: string) => rows.find((r) => r.key === k)?.definition.text ?? k;
  const why = closed
    ? 'This assessment is no longer taking answers.'
    : row.state.state === 'hidden'
      ? row.state.by
        ? `Skipped: ${row.state.by.label}, given your answer to “${textOf(row.state.by.from)}”.`
        : 'Not asked, given your other answers.'
      : row.state.state === 'implied'
        ? `Counted as not in place: ${row.state.by.label}, given your answer to “${textOf(row.state.by.from)}”. Change that answer to answer this one.`
        : row.state.state === 'waiting'
          ? `Answer ${row.state.on.map((k) => rows.find((r) => r.key === k)?.definition.text ?? k).join('; ')} first.`
          : row.stage && !row.stage.open && stage && journey
            ? `${stage.name}: ${stageNote(stage, journey)}`
            : row.state.state === 'visible' && !row.state.ask
              ? 'This one is filled in for you from your connected systems.'
              : null;

  const save = async (value: unknown) => {
    setErrors({});
    setNotice(null);
    onConflict(false);
    if (value === undefined) return setErrors({ '': 'Enter an answer, or skip this question.' });
    const check = value === null ? { ok: true as const } : checkAnswer(q, value, 'USER_DECLARED');
    if (!check.ok)
      return setErrors(Object.fromEntries(check.errors.map((e) => [e.path, plainMessage(e.message)])));
    try {
      const res = await answer.mutateAsync([{ question: row.key, value, based_on: row.based_on }]);
      const finished = res.stage_changes.filter((c) => c.to === 'completed');
      const s = finished.length
        ? res.journey?.stages.find((x) => x.key === finished.at(-1)!.stage)
        : undefined;
      onFeedback(s ? { title: `${s.name} done`, text: s.completion_message || 'Stage complete.' } : null);
      navigate(href.answer(id));
    } catch (err) {
      if (err instanceof ApiError && err.code === 'answer_conflict') onConflict(true);
      else if (err instanceof ApiError && err.issues.length) {
        const fields = valueErrors(err.issues);
        setErrors(fields);
        if (!Object.keys(fields).length) setNotice(err.issues.map((i) => i.message).join(' '));
      } else setNotice(err instanceof Error ? err.message : 'Could not save the answer.');
    }
  };

  const tapToSave = ['single_choice', 'boolean', 'rating'].includes(q.type) ||
    (q.type === 'entity_selector' && q.options.length > 0 && !q.settings.multiple); // prettier-ignore
  const unsupported = ['file_upload', 'image'].includes(q.type);
  const typed = !tapToSave && !unsupported;
  const left = remaining(rows);

  return (
    <form
      className="card qcard"
      aria-busy={answer.isPending}
      noValidate
      onSubmit={(e) => {
        // Enter in a typed answer saves it, as Continue does.
        e.preventDefault();
        if (typed && askable && !blocked) void save(draft);
      }}
    >
      <div className="row-between small">
        <span className="qtopic">
          {stage && <StageIcon state={stage.state} size={15} />}
          {stage ? `${stage.key} · ${stage.name}` : 'Question'}
        </span>
        {left.minutes > 0 && (
          <span className="muted">
            <Clock size={13} /> About {left.minutes} min left
          </span>
        )}
      </div>
      <h2 id={`q-${row.key}-label`}>
        <label htmlFor={`q-${row.key}`}>{q.text}</label>
        {!q.mandatory && <span className="badge optional">Optional</span>}
      </h2>
      {q.help && <p className="why">{q.help}</p>}

      {why && (
        <div className="notice neutral">
          <Info size={15} /> {why}
        </div>
      )}
      {!why && enough && !pressing(q) && (
        <div className="notice ok" role="status">
          <CheckCircle2 size={15} /> You’ve said enough for a reliable score. This one is optional: answering
          it sharpens your score, or <a href={href.assessment(id)}>stop here</a>.
        </div>
      )}
      {conflict && (
        <div className="notice" role="alert">
          Someone else answered this a moment ago. Their answer is shown now; change it if you need to.
        </div>
      )}
      {notice && (
        <div className="notice" role="alert">
          {notice}
        </div>
      )}

      <div className="answer-area" role="group" aria-labelledby={`q-${row.key}-label`}>
        <AnswerInput
          id={`q-${row.key}`}
          q={q}
          value={draft}
          onChange={setDraft}
          onPick={(v) => {
            setDraft(v);
            void save(v);
          }}
          errors={errors}
          disabled={blocked}
          upload={{ assessmentId: id, question: row.key }}
        />
        {tapToSave && errors[''] && <div className="error">{errors['']}</div>}
      </div>

      <div className="qnav">
        <button type="button" className="btn" onClick={() => history.back()}>
          <ArrowLeft size={15} /> Back
        </button>
        <div className="grow" />
        {row.answer && askable && !closed && (
          <button type="button" className="linklike" disabled={answer.isPending} onClick={() => save(null)}>
            Clear answer
          </button>
        )}
        {!q.mandatory && askable && !row.answer && (
          <button type="button" className="btn btn-ghost" onClick={onSkip}>
            Skip
          </button>
        )}
        {typed && askable && (
          <button type="submit" className="btn btn-primary" disabled={blocked}>
            {answer.isPending ? 'Saving…' : 'Continue'} <ArrowRight size={15} />
          </button>
        )}
        {(!askable || (unsupported && q.mandatory)) && (
          <a className="btn btn-primary" href={href.answer(id)}>
            Next question <ArrowRight size={15} />
          </a>
        )}
      </div>
    </form>
  );
}

export function Progress({
  rows,
  journey,
  current,
  feedback,
  readiness,
}: {
  rows: QuestionRow[];
  journey: JourneyRuntime | null;
  current?: QuestionRow;
  feedback: { title: string; text: string } | null;
  readiness?: Readiness;
}) {
  // prettier-ignore
  const answered = rows.filter(counts).length;
  return (
    <aside className="card live">
      <div className="eyebrow">Your progress</div>
      <div className="big-num">
        {answered}
        <span> answered</span>
      </div>
      {readiness && <ReadinessLine r={readiness} />}
      {feedback && (
        <div className="feedback" role="status">
          <CheckCircle2 size={16} />
          <div>
            <b>{feedback.title}</b>
            <p>{feedback.text}</p>
          </div>
        </div>
      )}
      {journey && (
        <ol className="stage-list">
          {journey.stages.map((s) => (
            <li key={s.key} className={`${s.state}${current?.stage?.key === s.key ? ' current' : ''}`}>
              <StageIcon state={s.state} size={16} />
              <span className="grow">{s.name}</span>
              {s.state !== 'locked' && s.state !== 'skipped' && (
                <span className="muted small">
                  {s.answered}/{s.asked}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      <p className="muted small">Answers are saved as you go. You can stop and come back any time.</p>
    </aside>
  );
}

/** How close the answers are to a reliable score (task 86). */
function ReadinessLine({ r }: { r: Readiness }) {
  if (r.enough)
    return (
      <p className="ready-line ok-t small" role="status">
        <CheckCircle2 size={14} /> Enough for a reliable score
      </p>
    );
  const n = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
  const parts = [
    r.required_left > 0 && n(r.required_left, 'required question', 'required questions'),
    r.kill_switches_left.length > 0 && n(r.kill_switches_left.length, 'key control', 'key controls'),
    r.required_left === 0 && r.controls_to_target > 0 && `about ${n(r.controls_to_target, 'more answer', 'more answers')}`,
  ].filter(Boolean);
  return <p className="ready-line muted small">For a reliable score: {parts.length ? `${parts.join(' and ')} to go` : 'nearly there'}</p>;
} // prettier-ignore

function Finished({
  id,
  rows,
  skipped,
  onRevisit,
}: {
  id: string;
  rows: QuestionRow[];
  skipped: Set<string>;
  onRevisit: () => void;
}) {
  // prettier-ignore
  const skippedNow = rows.filter((r) => skipped.has(r.key) && isAskable(r) && !r.answer).length;
  const left = remaining(rows);
  const finish = useFinish(id);
  // A business on its own finishes from here in one click; handing it to an insurer goes by the review.
  const oneStep = !left.mandatory && finish.own && finish.canSubmit;
  return (
    <section className="card qcard done">
      <PartyPopper size={32} className="ok-icon" />
      <h2>{left.mandatory ? 'Nothing more to answer right now' : 'All done for now'}</h2>
      <p className="why">
        {left.mandatory
          ? 'The remaining questions open once earlier stages are complete, or someone else is answering them.'
          : 'Everything required is answered. Thank you.'}
        {skippedNow > 0 &&
          ` You skipped ${skippedNow} optional ${skippedNow === 1 ? 'question' : 'questions'}.`}
      </p>
      <div className="row gap">
        {oneStep ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={finish.pending}
            onClick={() => void finish.run()}
          >
            <Send size={15} /> {finish.pending ? 'Finishing…' : 'Finish and get my report'}
          </button>
        ) : (
          <a className="btn btn-primary" href={href.assessment(id)}>
            {left.mandatory ? 'See your answers' : 'Review and submit'}
          </a>
        )}
        {skippedNow > 0 && (
          <button type="button" className="btn" onClick={onRevisit}>
            Answer the skipped ones
          </button>
        )}
      </div>
    </section>
  );
}
